import React, { PropTypes, PureComponent } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import Promise from 'bluebird';
import { Flags, SyndicatedPlayerLoginOptions, ContentStatus } from 'enums';
import WatchPropTypes from 'common/PropTypes';
import { canPlayContent, getAccessMethodFromAuthTypes, getContentId } from 'lib/auth';
import { isEmpty } from 'lib/util';
import * as oneId from 'lib/oneId';
import injectIntl, { intlShape } from 'common/injectIntl';
import { updateRoute, analyticsReady, fetchExternalConfig } from 'common/actions/appActions';
import { getEventByQuery } from 'common/actions/listingsActions';
import { domainCheck } from 'webapp/syndicated/actions';

import { setStatusFilter, fetchSyndicatedSchedule } from 'common/schedule/actions';
import {
    fetchEventOrVideoByQuery,
    createRequestPageAction,
    createReceivePageAction,
    completePageLoad
} from 'common/page/actions';
import {
    getProviders,
    createSmartAdobePassInitializeAction,
    createSmartIpAuthenticationAction
} from 'common/actions/authenticationActions';
import { checkOneIDAuthentication, checkSWIDAuthentication } from 'common/actions/oneIdActions';
import bootstrapSelector, { espnPlayerSrcFilesSelector } from 'common/selectors/bootstrapSelector';
import playerSelector from 'common/selectors/playerSelector';
import { userAuthSelector } from 'common/selectors/authenticationSelectors';
import providerSelector from 'common/selectors/providerSelector';
import pageSelector, { brandingSelector } from 'common/selectors/pageSelector';
import { adobePassEnvSelector, countrySelector, oneIdEnvSelector } from 'common/selectors/appSelector';
import heroSelector from 'common/selectors/heroSelector';
import routingSelector from 'common/selectors/routingSelector';
import SyndicatedPlayer, { domainAuthStatus } from 'webapp/syndicated/SyndicatedPlayer';
import SyndicatedScheduleModule from 'webapp/syndicated/SyndicatedScheduleModule';
import appConfig from '../../../config';
import MessagingContainer from 'webapp/modal/messaging/MessagingContainer';
import {
    BAM_PROXY_IFRAME_ID,
    BAM_PROMO_IFRAME_ID,
    BAM_PAYWALL_IFRAME_ID
} from 'constants';
import { createSmartFanAction } from 'common/actions/fanActions';

const {
    shellVersion,
    secureCdnHost,
    devStyles
} = appConfig;

const BASE_HREF = `${secureCdnHost}/redesign/${shellVersion}`;
let STYLE_HREF = `${BASE_HREF}/css/secure`;
if (devStyles) {
    STYLE_HREF = 'http://localhost:8080/static/css';
}

const { OneIdJs } = oneId;

const { NEW_WINDOW_MANAGED } = SyndicatedPlayerLoginOptions;

export class SyndicatedPlayerPageComponent extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            domainAuth: domainAuthStatus.UNKNOWN,
            hovered: false,
            overlayOpened: false
        };
        this.onParentHost = this.onParentHost.bind(this);
        this.onMouseOver = this.onMouseOver.bind(this);
        this.onMouseOut = this.onMouseOut.bind(this);
        this.onOverlayOpened = this.onOverlayOpened.bind(this);
        this.onOverlayClosed = this.onOverlayClosed.bind(this);
    }

    componentDidMount() {
        const { referrer = '' } = document;
        this.checkDomain(referrer);

        const { dispatch, content, routing: { locationBeforeTransitions } } = this.props;
        const { query = {} } = locationBeforeTransitions || {};
        let newQuery;
        dispatch(checkSWIDAuthentication());
        dispatch(createSmartFanAction());
        if (content) {
            delete query.eventId;
            delete query.gameId;
            delete query.league;
            newQuery = {
                ...query,
                ...getContentId(content)
            };
        } else if (query.id || query.eventId || query.channel || query.network) {
            newQuery = query;
        } else if (!query.partner) {
            newQuery = {
                ...query,
                network: 'espn'
            };
        }

        dispatch(updateRoute(newQuery));
    }

    onParentHost(href) {
        // this is a fallback when `document.referrer` is empty
        if (!this._domainChecked) {
            this.checkDomain(href);
        }
    }

    onMouseOver() {
        if (this.state.overlayOpened) {
            return;
        }
        this.setState({ hovered: true });
    }

    onMouseOut() {
        if (this.state.overlayOpened) {
            return;
        }
        this.setState({ hovered: false });
    }

    onOverlayOpened() {
        this.setState({ overlayOpened: true });
        this.setState({ hovered: false });
    }

    onOverlayClosed() {
        this.setState({ overlayOpened: false });
    }

    async checkDomain(href) {
        const { bootstrap: { syndicatedPlayer: { gatedAll = false, gatedIE = true } = {} }, dispatch, params } = this.props;
        const hostResult = /https?:\/\/([^\/:?#]+)/.exec(href);
        const host = ((hostResult && hostResult[1]) || '').replace(/^www\./, ''); // that's how hostname is stored in CMS
        let canAllowDomain;
        if (gatedAll || window.top === window.self) { // not in iframe, no need for domain check
            this._domainChecked = true;
            canAllowDomain = true;
        } else if (host) {
            this._domainChecked = true;
            const key = window.btoa(host);
            try {
                const { domainAllowed = 'false' } = await dispatch(domainCheck(key));
                canAllowDomain = domainAllowed === 'true';
            } catch (err) { // in the case of unexpected error, we open gate to everyone.
                console.error(err);
                canAllowDomain = true;
            }
        } else if (gatedIE) {
            this._domainChecked = true;
            // partner embeds the player directly with iframe, so can't pass parent location,
            // this should apply only for the old browser
            canAllowDomain = true;
        } else {
            console.warn('syndicated player: host unknown');
        }
        if (canAllowDomain != null) {
            if (canAllowDomain) {
                try {
                    // make sure each action doesn't throw, otherwise it exits as soon as it throws
                    await Promise.all([
                        dispatch(createSmartAdobePassInitializeAction(params)),
                        dispatch(createSmartIpAuthenticationAction()),
                        dispatch(checkOneIDAuthentication())
                    ]);
                } catch (err) {
                    console.error(err);
                }
            }
            this.setState({
                domainAuth: canAllowDomain ? domainAuthStatus.ALLOWED : domainAuthStatus.NOT_ALLOWED
            });
        }
    }

    render() {
        const { domainAuth, hovered, overlayOpened } = this.state;
        const {
            content,
            canPlayContent: canPlayContentFlag,
            branding,
            location,
            page,
            player,
            country,
            providerInfo,
            adobePassEnv,
            oneIdEnv,
            authStatus,
            espnPlayerSrc: {
                espnPlayerJsUrl,
                espnPlayerCssUrl
            },
            routing: { locationBeforeTransitions },
            messages,
            intl: { formatMessage }
        } = this.props;
        const { query = {} } = locationBeforeTransitions || {};
        const showScheduleModule = (
            country === 'US' &&
            (
                (query[Flags.PARTNER] && query[Flags.SHOW_SCHEDULE_IN_SYNDICATED_PLAYER] !== 'false') ||
                query[Flags.SHOW_SCHEDULE_IN_SYNDICATED_PLAYER] === 'true'
            ) &&
            domainAuth === domainAuthStatus.ALLOWED
        );
        const authRetry = !!query[Flags.AUTH_RETRY];
        const xdomainBlocked = !!query[Flags.XDOMAIN_BLOCKED];
        let errorMessage;
        if (query.partner && country !== 'US') {
            errorMessage = formatMessage(messages['syndicated.player.message.geo']);
        } else if (domainAuth === domainAuthStatus.NOT_ALLOWED) {
            errorMessage = formatMessage(messages['syndicated.player.domain.error']);
        } else if (isEmpty(content)) {
            errorMessage = formatMessage(messages['syndicated.player.message.no-events']);
        } else if (content && content.status === ContentStatus.UPCOMING) {
            errorMessage = formatMessage(messages['syndicated.player.message.upcoming']);
        }

        return (
            <div
                style={{ position: 'absolute', width: '100%', height: '100%' }}
                onMouseOver={this.onMouseOver}
                onMouseOut={this.onMouseOut}
            >
                <Helmet
                    meta={[
                        { name: 'robots', content: 'noindex, nofollow' }
                    ]}
                    link={[
                        { rel: 'stylesheet', href: `${STYLE_HREF}/page.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/watch.css` },
                        {
                            class: '__watch-player-bundle__',
                            rel: 'stylesheet',
                            href: espnPlayerCssUrl
                        }
                    ]}
                    style={[{
                        cssText: `
                            body {
                                overflow: hidden;
                            }
                            html, body, #app, .watch-root { width: 100%; height: 100%; margin: 0; padding: 0; }
                            .reload:before {
                                font-family: ESPNVideoIcons;
                                content:"\\e929";
                                padding-right: 4px;
                            }
                            .vjs-menu li { text-transform: none; }

                            #watchespn-bam-player {
                                background-color: #000 !important;
                                height: 100% !important;
                                left: 0;
                                position: absolute;
                                top: 0;
                                width: 100% !important;
                                z-index: 1000031;
                            }

                            #watchespn-player iframe,
                            #watchespn-bam-player iframe{
                                width: 100%;
                                height: 100%;
                                border: none;
                            }

                            .${BAM_PROXY_IFRAME_ID}, .${BAM_PROXY_IFRAME_ID} iframe {
                                position: absolute;
                                left: -10000px;
                                width: 1px;
                                height: 1px;
                                border: none;
                            }

                            #${BAM_PROMO_IFRAME_ID} {
                                width: 100%;
                                height: 506.25px;
                                margin-bottom: 10px;
                            }

                            #${BAM_PROMO_IFRAME_ID} iframe,
                            #${BAM_PAYWALL_IFRAME_ID} iframe {
                                height: 100% !important;
                                left: 0;
                                position: absolute;
                                top: 0;
                                width: 100% !important;
                                z-index: 1000033;
                            }
                        `
                    }]}
                    script={[
                        {
                            src: OneIdJs[oneIdEnv]
                        },
                        {
                            src: espnPlayerJsUrl,
                            async: true
                        }
                    ]}
                />
                <SyndicatedPlayer
                    content={content}
                    canPlayContent={canPlayContentFlag}
                    branding={branding}
                    query={query}
                    location={location}
                    page={page}
                    player={player}
                    providerInfo={providerInfo}
                    adobePassEnv={adobePassEnv}
                    authStatus={authStatus}
                    loginOption={NEW_WINDOW_MANAGED}
                    domainAuth={domainAuth}
                    authRetry={authRetry}
                    xdomainBlocked={xdomainBlocked}
                    errorMessage={errorMessage}
                    onParentHost={this.onParentHost}
                    onSocialOpened={this.onOverlayOpened}
                    onSocialClosed={this.onOverlayClosed}
                    onCaptionOpened={this.onOverlayOpened}
                    onCaptionClosed={this.onOverlayClosed}
                />
                {showScheduleModule && (
                    <SyndicatedScheduleModule
                        location={location}
                        hovered={hovered}
                        hidden={overlayOpened}
                    />
                )}

                <MessagingContainer />
            </div>
        );
    }
}

SyndicatedPlayerPageComponent.propTypes = {
    bootstrap: PropTypes.object,
    dispatch: PropTypes.func,
    params: PropTypes.object,
    content: WatchPropTypes.content,
    canPlayContent: PropTypes.bool,
    branding: PropTypes.object,
    location: PropTypes.object,
    routing: PropTypes.object,
    page: PropTypes.object,
    country: PropTypes.string,
    player: PropTypes.object,
    providerInfo: PropTypes.object,
    adobePassEnv: PropTypes.string,
    oneIdEnv: PropTypes.string,
    authStatus: PropTypes.string,
    intl: intlShape.isRequired,
    messages: PropTypes.object.isRequired,
    espnPlayerSrc: PropTypes.object.isRequired
};

SyndicatedPlayerPageComponent.defaultProps = {
    content: {},
    providers: []
};

SyndicatedPlayerPageComponent.loadActions = [
    fetchExternalConfig,
    getProviders,
    (params, { type = 'live' }) => setStatusFilter(type),
    (pathParams, query) => async (dispatch) => {
        const id = 'syndicated-player';
        const name = 'SyndicatedPlayer';
        dispatch(createRequestPageAction(id));
        dispatch(completePageLoad());
        dispatch(analyticsReady({}));

        const eventId = query.eventId || query.gameId;
        const { partner, dates, thumbnail } = query;
        let content;
        if (!query.id && partner) {
            if (query.categoryId) {
                content = await dispatch(fetchSyndicatedSchedule(query));
                content = content.filter(({ broadcasts, subcategories }) => {
                    const broadcastMatch = (
                        broadcasts[0] &&
                        broadcasts[0].abbreviation &&
                        broadcasts[0].abbreviation.match(/3|_dtc/i)
                    );

                    // Only listings that match the `partner` query param, if provided
                    let partnerMatch = true;

                    if (partner && subcategories[0]) {
                        const league = subcategories[0];
                        const partnerRegEx = new RegExp(`${partner}|${partner.replace('-', ' ')}`, 'i');
                        const codeMatch = (league.code && league.code.match(partnerRegEx));
                        const idMatch = (league.id && league.id.toLowerCase() === partner.toLowerCase());
                        const nameMatch = !!(league.name && league.name.match(partnerRegEx));

                        partnerMatch = (codeMatch || idMatch || nameMatch);
                    }

                    return (broadcastMatch && partnerMatch);
                })[0];
            } else {
                content = await dispatch(getEventByQuery({ partner, dates }));
            }
        }

        let newQuery;
        delete query.thumbnail;
        if (content) {
            delete query.eventId;
            delete query.gameId;
            delete query.league;
            delete query.partner;
            delete query.dates;
            newQuery = { ...query, id: content.id };
        } else if (eventId) {
            newQuery = { eventId };
        } else if (query.id || query.channel || query.network) {
            newQuery = query;
        } else if (!partner) {
            newQuery = {
                ...query,
                network: 'espn'
            };
        }

        let payload;
        if (newQuery) {
            payload = await dispatch(fetchEventOrVideoByQuery(newQuery));
        }
        const { page: { edition, contents: heroContent } = {} } = payload || {};
        if (heroContent && thumbnail) {
            heroContent.imageHref = thumbnail;
        }
        dispatch(createReceivePageAction({
            id,
            name,
            edition,
            heroContent,
            isFinal: true
        }));

        if (!partner) {
            dispatch(updateRoute(newQuery));
        }
    }
];

// it's important to return hero content so that `disableAuthPrompt` gets evaluated correctly in `authenticationAware`
const mapStateToProps = (state) => {
    const { schedule } = state;
    const userAuth = userAuthSelector(state);
    const content = heroSelector(state);
    const page = pageSelector(state);
    // this will return `true` when `selectedContent` is empty on purpose
    const canPlayContentFlag = (
        canPlayContent(content, userAuth, false) &&
        !isEmpty(content) &&
        content.status !== ContentStatus.UPCOMING
    );
    const authStatus = getAccessMethodFromAuthTypes(userAuth);
    return {
        bootstrap: bootstrapSelector(state),
        content,
        canPlayContent: canPlayContentFlag,
        branding: brandingSelector(state),
        page: { id: page.id, name: page.name },
        routing: routingSelector(state),
        player: playerSelector(state),
        espnPlayerSrc: espnPlayerSrcFilesSelector(state),
        country: countrySelector(state),
        authStatus,
        providerInfo: providerSelector(state),
        adobePassEnv: adobePassEnvSelector(state),
        oneIdEnv: oneIdEnvSelector(state),
        selectedTab: schedule.statusFilterValue,
        listings: schedule.listings || [],
        loading: schedule.loading
    };
};

export default connect(mapStateToProps)(injectIntl(SyndicatedPlayerPageComponent));



// WEBPACK FOOTER //
// ./app/webapp/page/SyndicatedPlayerPage.js